/**
 * Chat notification labelling
 *
 * Shared logic for turning a chat message (or its durable server notification)
 * into a customer-facing label. Reused by:
 *   - `useChatRealtime` — the transient sonner toast title, and
 *   - `NotificationPanel` — the bell row title for `chat_message` notifications
 *     (whose stored `title` is a generic "New message").
 *
 * BRANDING / PRIVACY: support-ticket messages are ALWAYS branded
 * "Studio1 Support". The wire payloads never carry an operator's real name, and
 * this label guarantees one can't leak onto the customer surface even if a
 * channel display name were somehow present.
 */

import type { QueryClient } from "@tanstack/react-query";
import { CHATS_ROOT_KEY } from "@/hooks/useChat";
import type { ChatChannelListItem } from "@/types/chat";

/** Customer-facing label for support-ticket messages (never an operator name). */
export const SUPPORT_LABEL = "Studio1 Support";

/** Fallback used when no better channel label is available. */
export const DEFAULT_CHAT_LABEL = "New message";

/**
 * Find a channel across any cached chat-list variant (archived / not). Reads the
 * `list_chats` cache only — never triggers a fetch.
 */
export function findCachedChannel(
  queryClient: QueryClient,
  channelId: string | undefined,
): ChatChannelListItem | undefined {
  if (!channelId) return undefined;
  const cached = queryClient.getQueriesData<ChatChannelListItem[]>({
    queryKey: CHATS_ROOT_KEY,
  });
  for (const [, list] of cached) {
    const found = list?.find((c) => c.channel_id === channelId);
    if (found) return found;
  }
  return undefined;
}

/**
 * Resolve a customer-facing title for a chat notification.
 *  - support (a `ticketId` is present) → branded "Studio1 Support"
 *  - DM / channel → the channel's DB-derived `display_name` from the chats cache
 *  - fallback → "New message"
 */
export function resolveChatNotificationTitle(params: {
  queryClient: QueryClient;
  channelId: string | undefined;
  ticketId: string | undefined;
}): string {
  const { queryClient, channelId, ticketId } = params;
  if (ticketId) return SUPPORT_LABEL;
  const channel = findCachedChannel(queryClient, channelId);
  return channel?.display_name?.trim() || DEFAULT_CHAT_LABEL;
}
